import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Brain, 
  ChevronDown, 
  ChevronUp, 
  Terminal, 
  Sparkles,
  Clock,
  Cpu,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keywords to highlight in the reasoning trace
const HIGHLIGHT_KEYWORDS = {
  risk: ['compliance risk', 'risk factor', 'red flag', 'warning', 'concern', 'gap', 'weakness', 'threat'],
  positive: ['strength', 'advantage', 'opportunity', 'strong', 'excellent', 'aligned', 'meets', 'exceeds'],
  analysis: ['analysis', 'evaluation', 'assessment', 'scoring', 'calculated', 'determined', 'conclusion'],
  decision: ['go', 'no-go', 'conditional', 'recommend', 'decision', 'verdict']
};

// Escape HTML so LLM output cannot inject markup/scripts before we run the
// keyword-highlighting regex replacements. Without this, a model emitting
// `<script>` or `<img onerror=...>` would be rendered as live HTML via
// dangerouslySetInnerHTML below.
const escapeHtml = (text) => {
  if (text === null || text === undefined) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Highlight keywords in text
const highlightText = (text) => {
  if (!text) return '';

  let result = escapeHtml(text);
  
  // Apply highlighting with HTML spans
  HIGHLIGHT_KEYWORDS.risk.forEach(keyword => {
    const regex = new RegExp(`(${keyword})`, 'gi');
    result = result.replace(regex, '<span class="text-red-600 font-semibold bg-red-50 px-1 rounded">$1</span>');
  });

  HIGHLIGHT_KEYWORDS.positive.forEach(keyword => {
    const regex = new RegExp(`(${keyword})`, 'gi');
    result = result.replace(regex, '<span class="text-emerald-600 font-semibold bg-emerald-50 px-1 rounded">$1</span>');
  });

  HIGHLIGHT_KEYWORDS.analysis.forEach(keyword => {
    const regex = new RegExp(`(${keyword})`, 'gi');
    result = result.replace(regex, '<span class="text-blue-600 font-semibold">$1</span>');
  });

  HIGHLIGHT_KEYWORDS.decision.forEach(keyword => {
    const regex = new RegExp(`\\b(${keyword})\\b`, 'gi');
    result = result.replace(regex, '<span class="text-violet-600 font-bold">$1</span>');
  });

  return result;
};

// Format the reasoning trace for display
const formatReasoningTrace = (trace) => {
  if (!trace) return [];
  
  // Split by numbered steps or bullet points
  const lines = trace.split(/\n/).filter(line => line.trim());
  
  return lines.map((line, index) => {
    // Detect if this is a heading/step
    const isStep = /^\d+\.|^Step \d+|^##|^\*\*/.test(line.trim());
    const isSubPoint = /^[-•]|^\s{2,}/.test(line);
    
    return {
      id: index,
      text: line.trim(),
      isStep,
      isSubPoint,
      highlighted: highlightText(line.trim())
    };
  });
};

export default function ReasoningTraceViewer({
  pursuitId,
  // authToken is accepted for backward compatibility with callers that still
  // pass it, but we no longer use it: auth is handled by the HttpOnly cookie
  // plus the backend cookie→Authorization bridge middleware, so every fetch
  // from this component just uses credentials: 'include'.
  // eslint-disable-next-line no-unused-vars
  authToken,
  defaultExpanded = false,
  className
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [loading, setLoading] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  // Retry cooldown prevents users from spamming the endpoint by rapidly
  // clicking "Retry" on a transient error. Simple millisecond timestamp guard.
  const lastFetchAt = useRef(0);
  const RETRY_COOLDOWN_MS = 1500;

  const fetchReasoningLog = useCallback(async ({ force = false } = {}) => {
    if (!pursuitId) return;
    const now = Date.now();
    if (!force && now - lastFetchAt.current < RETRY_COOLDOWN_MS) {
      return; // Silently drop — retry cooldown
    }
    lastFetchAt.current = now;
    setLoading(true);
    setError(null);

    try {
      // Cache-bust on force-refresh so any stale CDN/browser cache is bypassed.
      const url = force
        ? `${API_URL}/api/llm/reasoning-log/${pursuitId}?_t=${now}`
        : `${API_URL}/api/llm/reasoning-log/${pursuitId}`;
      const response = await fetch(url, {
        credentials: 'include',
        cache: force ? 'no-store' : 'default',
      });

      if (response.ok) {
        const data = await response.json();
        setTraceData(data);
      } else if (response.status === 401) {
        setError('Your session expired. Please sign in again.');
      } else {
        let detail = 'Failed to load reasoning trace';
        try {
          const errorData = await response.json();
          if (typeof errorData?.detail === 'string' && errorData.detail.length < 200) {
            detail = errorData.detail;
          }
        } catch {
          // ignore — fall back to generic message
        }
        setError(detail);
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching reasoning log:', err);
    } finally {
      setLoading(false);
    }
  }, [pursuitId]);

  // v3.11.15 — Reset cached trace whenever the active pursuit changes so a
  // user navigating between pursuits never sees a previous pursuit's trace.
  useEffect(() => {
    setTraceData(null);
    setError(null);
    lastFetchAt.current = 0;
  }, [pursuitId]);

  // v3.11.15 — Auto-refetch when an upstream signal (Scorecard / Cap Matrix /
  // RTM / AI Analysis) updates this pursuit. Previously the trace was fetched
  // once on first expand and never refreshed, so re-running AI analysis left
  // the pane showing a stale ("stagnant") trace until the user reloaded.
  useEffect(() => {
    if (!pursuitId) return;
    const handler = (evt) => {
      if (evt?.detail?.pursuitId && evt.detail.pursuitId !== pursuitId) return;
      // Invalidate cache; refetch only if the panel is currently expanded so
      // we don't hammer the endpoint when the user has the trace collapsed.
      setTraceData(null);
      if (isExpanded) {
        fetchReasoningLog({ force: true });
      }
    };
    window.addEventListener('pwin-signal-updated', handler);
    return () => window.removeEventListener('pwin-signal-updated', handler);
  }, [pursuitId, isExpanded, fetchReasoningLog]);

  // Fetch reasoning log when expanded
  useEffect(() => {
    if (isExpanded && pursuitId && !traceData && !loading) {
      fetchReasoningLog();
    }
  }, [isExpanded, pursuitId, traceData, loading, fetchReasoningLog]);

  const handleCopyTrace = async () => {
    if (traceData?.reasoning_log) {
      await navigator.clipboard.writeText(traceData.reasoning_log);
      setCopied(true);
    }
  };

  // Auto-dismiss the "Copied" state with proper cleanup on unmount / re-click,
  // so React doesn't warn about setting state on an unmounted component.
  useEffect(() => {
    if (!copied) return;
    const timeoutId = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  const formattedTrace = traceData?.reasoning_log ? formatReasoningTrace(traceData.reasoning_log) : [];

  return (
    <Card className={cn("border-slate-200 bg-white shadow-sm overflow-hidden", className)} data-testid="reasoning-trace-card">
      {/* Header - Always visible */}
      <CardHeader 
        className={cn(
          "pb-3 cursor-pointer transition-colors",
          isExpanded ? "bg-slate-900 text-white" : "bg-gradient-to-r from-violet-50 to-slate-50 hover:from-violet-100 hover:to-slate-100"
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              isExpanded ? "bg-violet-600" : "bg-violet-100"
            )}>
              <Brain className={cn("w-5 h-5", isExpanded ? "text-white" : "text-violet-600")} />
            </div>
            <div>
              <CardTitle className={cn(
                "text-base font-semibold flex items-center gap-2",
                isExpanded ? "text-white" : "text-slate-900"
              )} style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Terminal className="w-4 h-4" />
                AI Reasoning Trace
                {traceData?.has_reasoning_log && (
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    isExpanded ? "bg-emerald-600 text-white border-emerald-500" : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  )}>
                    Available
                  </Badge>
                )}
              </CardTitle>
              <p className={cn("text-xs mt-0.5", isExpanded ? "text-slate-300" : "text-slate-500")}>
                {traceData?.model_used
                  ? `${traceData.model_used}'s internal decision-making process`
                  : "AI model's internal decision-making process"}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className={isExpanded ? "text-white hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"}
            data-testid="toggle-reasoning-trace"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
      </CardHeader>

      {/* Expandable Content */}
      {isExpanded && (
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 bg-slate-900">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              <span className="ml-2 text-slate-400">Loading reasoning trace...</span>
            </div>
          ) : error ? (
            <div className="p-6 bg-slate-900 text-center">
              <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-300">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 text-slate-300 border-slate-600 hover:bg-slate-800"
                onClick={fetchReasoningLog}
              >
                Retry
              </Button>
            </div>
          ) : !traceData?.has_reasoning_log || !(traceData?.reasoning_log || '').trim() ? (
            <div className="p-6 bg-slate-900 text-center">
              <Cpu className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-400 mb-1">
                {traceData?.has_reasoning_log
                  ? 'Reasoning trace is empty'
                  : 'No reasoning trace available'}
              </p>
              <p className="text-xs text-slate-500 mb-3">
                {traceData?.has_reasoning_log
                  ? 'The AI analysis completed but did not record its thinking process. Try re-running the analysis.'
                  : "Run a Go/No-Go AI analysis for this pursuit to capture the model's thinking process."}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="text-slate-300 border-slate-600 hover:bg-slate-800"
                onClick={() => fetchReasoningLog({ force: true })}
                disabled={loading}
                data-testid="refresh-empty-trace-btn"
              >
                <RefreshCw className={cn('w-3.5 h-3.5 mr-1.5', loading && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          ) : (
            <>
              {/* Metadata Bar */}
              <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Cpu className="w-3.5 h-3.5" />
                    {traceData.model_used || 'AI model'}
                  </span>
                  {traceData.analyzed_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(traceData.analyzed_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white hover:bg-slate-700 h-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchReasoningLog({ force: true });
                    }}
                    disabled={loading}
                    data-testid="refresh-trace-btn"
                    title="Refresh — re-fetch the latest reasoning trace from the server"
                  >
                    <RefreshCw className={cn('w-3.5 h-3.5 mr-1', loading && 'animate-spin')} />
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white hover:bg-slate-700 h-7"
                    onClick={handleCopyTrace}
                    data-testid="copy-trace-btn"
                  >
                    {copied ? (
                      <><Check className="w-3.5 h-3.5 mr-1 text-emerald-400" /> Copied</>
                    ) : (
                      <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Terminal-style Trace Display */}
              <div 
                className="bg-slate-900 p-4 max-h-[500px] overflow-y-auto font-mono text-sm"
                data-testid="reasoning-trace-content"
              >
                {/* Legend — shown above content so it remains visible as the
                    user scrolls through long reasoning traces. */}
                <div className="mb-4 pb-3 border-b border-slate-700">
                  <p className="text-xs text-slate-500 mb-2">Keyword Legend:</p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="text-red-400">● Risk Factors</span>
                    <span className="text-emerald-400">● Strengths</span>
                    <span className="text-blue-400">● Analysis</span>
                    <span className="text-violet-400">● Decision Points</span>
                  </div>
                </div>

                <div className="space-y-2">
                  {formattedTrace.map((line) => (
                    <div 
                      key={line.id}
                      className={cn(
                        "leading-relaxed",
                        line.isStep ? "text-violet-400 font-semibold mt-4 first:mt-0" : "",
                        line.isSubPoint ? "pl-4 text-slate-300" : "text-slate-300"
                      )}
                    >
                      {line.isStep && (
                        <span className="text-emerald-400 mr-2">▸</span>
                      )}
                      <span 
                        dangerouslySetInnerHTML={{ __html: line.highlighted }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
